import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { authStore, isLoggedIn } from "@workspace/shared/core/store/auth/auth.store";
import { getEnv } from "@workspace/shared/core/env";
import profileServices from "@workspace/shared/core/services/profile/profile.services";
import {
  SESSION_EXPIRED_EVENT,
  getDeviceId,
  type SessionExpiredReason,
} from "@workspace/shared/core/services/api";
import { flushAllPendingAppsForLogout, hasAnyPendingUpload } from "@workspace/shared/core/store/globalPendingSync";
import { getAppKey, selectedAppStore } from "@workspace/shared/core/store/selected-app/selected-app.store";
import {
  CLOSE_FLUSH_BUDGET_MS,
  SESSION_CLOSED_NOTICE_KEY,
  closedApp,
  isCloseForThisApp,
} from "./sessionClosedPolicy.ts";

/**
 * Whether a `session:released` is about ANOTHER device. The server spares the
 * device that asked for "close the other sessions" — by room when its socket
 * sent a device id, and by naming it in the payload (`exceptDeviceId`) for a
 * socket that didn't. That device keeps its session and must not act on it.
 */
export const isReleaseForAnotherDevice = (
  payload: unknown,
  myDeviceId: string | null | undefined
): boolean => {
  if (!myDeviceId || !payload || typeof payload !== "object") return false;
  const exceptDeviceId = (payload as { exceptDeviceId?: unknown }).exceptDeviceId;
  return typeof exceptDeviceId === "string" && exceptDeviceId !== "" && exceptDeviceId === myDeviceId;
};

/**
 * How long the API may still call a just-revoked session live: its auth guard
 * caches a live check per device for 10 s (per instance).
 */
const RELEASE_RECHECK_DELAY_MS = 11_000;

/** Whether the server still takes this device's session, asked once the
 *  guard's cache can't answer for it. */
export const sessionStillAnswers = async (): Promise<boolean> => {
  await new Promise((resolve) => setTimeout(resolve, RELEASE_RECHECK_DELAY_MS));
  try {
    await profileServices.onGetProfile();
    return true;
  } catch {
    return false;
  }
};

/**
 * This device was signed out from another one: ask for the password right
 * here (AuthContext's prompt) — the coach stays on the rep they are grading,
 * nothing is cleared, and what waits on the device uploads once they are
 * back in. Only with nobody signed in does it fall back to the dashboard.
 *
 * Every device of the account hears a release. One with nothing to revoke —
 * on the dashboard (it registers no session), or an app whose register
 * failed — is still signed in, and was asked for its password for nothing: the
 * server is asked first (audit 2026-10-19).
 */
export const handleSessionReleased = async (
  payload: unknown,
  myDeviceId: string | null,
  stillSignedIn: () => Promise<boolean> = sessionStillAnswers
): Promise<void> => {
  if (isReleaseForAnotherDevice(payload, myDeviceId)) return;
  if (isLoggedIn()) {
    if (await stillSignedIn()) return;
    window.dispatchEvent(
      new CustomEvent<{ reason: SessionExpiredReason }>(SESSION_EXPIRED_EVENT, {
        detail: { reason: "released" },
      })
    );
    return;
  }
  window.location.href = "/";
};

/** Every mounted subscriber hears a close; the first one acts on it. */
let closingSeat = false;

/**
 * Handle a `session:closed` for THIS device: its seat in the app it is in was
 * closed from another device (client 2026-11-04, as in-game). Go back to the
 * dashboard, still signed in — closing is never a sign-out.
 *
 * Pending plays get a bounded upload attempt first, so most land before
 * leaving. Whatever doesn't stays on the device (nothing is cleared) and
 * uploads from the dashboard, which syncs every app's pending plays — the
 * session is alive, so leaving loses nothing.
 */
export const handleSessionClosed = async (
  payload: unknown,
  leave: () => void = () => {
    window.location.href = "/";
  }
): Promise<void> => {
  if (!isCloseForThisApp(payload, getAppKey())) return;
  if (closingSeat) return;
  closingSeat = true;
  try {
    const app = closedApp(payload) as string;
    if (hasAnyPendingUpload()) {
      try {
        await Promise.race<unknown>([
          flushAllPendingAppsForLogout(),
          new Promise((resolve) => setTimeout(resolve, CLOSE_FLUSH_BUDGET_MS)),
        ]);
      } catch (error) {
        console.warn("[session] upload before a closed session failed; the dashboard will retry", error);
      }
    }
    try {
      sessionStorage.setItem(SESSION_CLOSED_NOTICE_KEY, app);
    } catch {
      // Private mode: the dashboard just won't say why.
    }
    selectedAppStore.getState().setSelectedAppState({ key: "appKey", value: "Dashboard" });
    leave();
  } finally {
    // The page is going; should it not (a test), the next close still acts.
    closingSeat = false;
  }
};

/** `navigator.onLine`, tracked (no provider needed: mounted at app-shell level). */
const useBrowserOnline = (): boolean => {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
};

/**
 * useSessionSocket — live session kick, ported from the in-game app.
 *
 * Connects to the API's session gateway; when this device's session is
 * revoked (a takeover on another device, an admin revoke), the backend emits
 * `session:released` and the coach is asked to sign in again in place. It used
 * to hard-redirect to the dashboard, which dropped the coach out of grading —
 * even on the device that ASKED for the other sessions to close, since this
 * socket never told the server which device it was.
 *
 * Reconnects with a new token (a refresh or an in-place sign-in: the server
 * refuses a stale one with "io server disconnect", which never retries) and
 * after an outage; stays closed offline.
 */
export const useSessionSocket = (opts?: {
  /**
   * Fired on `session:changed` — a session was registered or released
   * somewhere for this user. Refetch-only by design (never redirect), so
   * the device that caused the change can't reload-loop itself.
   */
  onSessionsChanged?: () => void;
}): Socket | null => {
  const accessToken = authStore((state) => state.accessToken);
  const isOnline = useBrowserOnline();
  const socketRef = useRef<Socket | null>(null);
  // Ref'd so a changing callback identity never reconnects the socket.
  const onChangedRef = useRef(opts?.onSessionsChanged);
  onChangedRef.current = opts?.onSessionsChanged;

  useEffect(() => {
    if (!accessToken || !isOnline) return;

    let apiUrl = "";
    try {
      apiUrl = getEnv().API_URL;
    } catch {
      return; // env not initialized (shouldn't happen post-boot)
    }
    if (!apiUrl) return;

    let cancelled = false;
    let socket: Socket | null = null;
    const onSessionChanged = () => {
      onChangedRef.current?.();
    };

    // The SAME id the REST client sends as x-device-id (capped, never
    // rejects), so the server can tell this device apart.
    void getDeviceId()
      .catch(() => null)
      .then((deviceId) => {
        if (cancelled) return;
        socket = io(apiUrl, {
          // Its own connection: socket.io caches one manager per origin and
          // reuses ITS options, so a reconnect with a new token (or a second
          // caller) would still send the first token.
          forceNew: true,
          // The API runs on AWS App Runner, which does NOT support WebSocket
          // connections: the polling handshake succeeds, then every websocket
          // upgrade attempt fails with a console error and a reconnect cycle.
          // Pin long-polling (what the connection always ended up on anyway) so
          // it's quiet and stable.
          transports: ["polling"],
          upgrade: false,
          extraHeaders: {
            authorization: `Bearer ${accessToken}`,
            ...(deviceId ? { "x-device-id": deviceId } : {}),
          },
          ...(deviceId ? { query: { deviceId } } : {}),
          // A retry budget instead of socket.io's 1 s → 5 s forever: on a
          // half-open sideline link every poll times out, and this control
          // can wait.
          reconnectionDelay: 5000,
          reconnectionDelayMax: 60000,
          randomizationFactor: 0.5,
        });
        socketRef.current = socket;
        socket.on("session:released", (payload: unknown) => {
          void handleSessionReleased(payload, deviceId);
        });
        socket.on("session:changed", onSessionChanged);
        socket.on("session:closed", (payload: unknown) => {
          void handleSessionClosed(payload);
        });
      });

    return () => {
      cancelled = true;
      socket?.off("session:released");
      socket?.off("session:changed", onSessionChanged);
      socket?.off("session:closed");
      socket?.disconnect();
      socketRef.current = null;
    };
  }, [accessToken, isOnline]);

  return socketRef.current;
};

export default useSessionSocket;
